import { useState } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Warehouse, Pencil, Plus, Trash2 } from "lucide-react";

export function WarehouseCards({ warehouses, canEdit, onSaved }) {
  const { t } = useI18n();
  const [edit, setEdit] = useState(null);
  const isNew = edit && !edit.id;

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/warehouses", { code: edit.code || "", name: edit.name || "", branch: edit.branch || "", address: edit.address || "", phone: edit.phone || "", email: edit.email || "" });
        toast.success(t("wh.created"));
      } else {
        await api.put(`/warehouses/${edit.code}`, { name: edit.name, address: edit.address || "", phone: edit.phone || "", email: edit.email || "" });
        toast.success(t("common.saved"));
      }
      setEdit(null);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (w) => {
    if (!window.confirm(`${t("wh.deleteConfirm")} ${w.code}?`)) return;
    try { await api.delete(`/warehouses/${w.code}`); toast.success(t("common.deleted")); onSaved?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const field = (k, label, type = "text") => (
    <div className="space-y-1">
      <Label className="text-[10px] font-bold uppercase tracking-widest">{label}</Label>
      <Input type={type} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="rounded-none h-8 text-xs" data-testid={`wh-form-${k}`} />
    </div>
  );

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 mb-5" data-testid="warehouse-cards">
      {canEdit && (
        <button type="button" onClick={() => setEdit({ code: "", name: "", branch: "", address: "", phone: "", email: "" })} data-testid="wh-new-button"
          className="border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 p-3 flex items-center justify-center gap-2 text-xs font-bold text-primary transition-colors min-h-[72px]">
          <Plus size={16} /> {t("wh.new")}
        </button>
      )}
      {warehouses.length === 0 && !canEdit && <div className="text-xs text-muted-foreground italic">{t("wh.none")}</div>}
      {warehouses.map((w) => (
        <div key={w.id} className="border bg-card p-3 flex gap-3" data-testid={`wh-card-${w.code}`}>
          <Warehouse size={18} className="text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-xs">
            <div className="font-black uppercase tracking-wide">{w.code} <span className="font-medium text-muted-foreground normal-case">— {w.name}</span></div>
            <div className="text-muted-foreground mt-1 truncate" data-testid={`wh-address-${w.code}`}>{w.address || <span className="italic">{t("wh.noAddress")}</span>}</div>
            {(w.phone || w.email) && <div className="text-muted-foreground truncate">{[w.phone, w.email].filter(Boolean).join(" · ")}</div>}
          </div>
          {canEdit && (
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none" onClick={() => setEdit({ ...w })} data-testid={`wh-edit-${w.code}`}><Pencil size={12} /></Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-muted-foreground hover:text-destructive" onClick={() => remove(w)} data-testid={`wh-delete-${w.code}`}><Trash2 size={12} /></Button>
            </div>
          )}
        </div>
      ))}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-none">
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{isNew ? t("wh.new") : `${t("wh.edit")} · ${edit.code}`}</DialogTitle>
                <DialogDescription className="text-xs">{isNew ? t("wh.newHint") : t("wh.editHint")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {isNew && field("code", t("wh.code"))}
                {isNew && field("branch", t("wh.branch"))}
                <div className="col-span-2">{field("name", t("wh.name"))}</div>
                <div className="col-span-2">{field("address", t("set.companyAddress"))}</div>
                {field("phone", t("set.companyPhone"))}
                {field("email", t("wh.email"), "email")}
              </div>
              <DialogFooter>
                <Button onClick={save} className="rounded-none text-xs font-bold" data-testid="wh-form-save">{t("common.save")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
